/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import Modal from '~/components/Modal'
import { useServiceContext } from '~/root'
import { File } from '~/constants/file'
import { useNavigate, useParams } from 'react-router'
import {
  undeleteFile,
  makeMultipleFilesUnobsolete,
  unobsoleteAllFilesInFolder,
  unobsoleteAllFilesFromPropertiesSearch
} from '~/services/api/files'
import { useEsignature } from '~/hooks/useEsignature'
import _ from 'lodash'

interface MakeUnObsoleteModalProps {
  fileDetails: any
  options?: any
  onClose?: () => void
}

const MakeUnObsoleteModal: React.FC<MakeUnObsoleteModalProps> = ({ fileDetails, options = {}, onClose }) => {
  const context = useServiceContext()
  const { toast, auth } = context.service
  const { $rootScope } = context
  const { translate } = context.service.filter
  const navigate = useNavigate()
  const params = useParams()
  const { signAction } = useEsignature()
  const [isOpen, setIsOpen] = useState(true)

  const doClose = (): void => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const isMultiselect = (): boolean => {
    return options?.selectedIds && options?.selectedIds?.length > 1
  }

  const save = async (): Promise<void> => {
    doClose()

    let successMsg = translate('BROWSER_MAKE_UNOBSOLETE_SUCCESS_MSG')
    if (!isMultiselect() && fileDetails.type === File.type.FOLDER) {
      successMsg = translate('BROWSER_MAKE_UNOBSOLETE_FOLDER_SUCCESS_MSG')
    }

    try {
      // Get e-signature if required
      let esignature: any = {}
      if (auth.requireESignatureForFileCommands?.()) {
        esignature = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_MAKE_UNBSOLETE')
      }

      // Perform unobsolete operation
      if (options && options.selectedWholeResult) {
        // Whole result selection - requires special API endpoints
        if (options.isPropertiesSearch && options.meta && options.filter && options.filter.metadataCriteria) {
          // Properties search - unobsolete all matching files
          const filter = { ...options.filter, workflowIsNull: true }
          await unobsoleteAllFilesFromPropertiesSearch({
            propertySearchRequestDTO: { filter },
            esignatureDTO: esignature
          })
        } else if (options.parentFolderId) {
          // Folder - unobsolete all files in folder
          await unobsoleteAllFilesInFolder(options.parentFolderId, {
            esignatureDTO: esignature,
            filter: options.filter
          })
        }
      } else {
        // Single file or multi-selection
        const fileId = isMultiselect() ? options?.selectedIds : fileDetails.id

        if (_.isArray(fileId)) {
          // Multiple files unobsolete
          await makeMultipleFilesUnobsolete({
            ids: fileId,
            esignature
          })
        } else {
          // Single file unobsolete
          await undeleteFile(fileId, { relayState: esignature.relayState })
        }
      }

      // Success - emit events and show toast
      toast.success(successMsg)
      // Emit global events for backward compatibility (window event bus)
      window.dispatchEvent(new CustomEvent('updateFiles', { detail: 'UNDELETE_FSI' }))
      window.dispatchEvent(new CustomEvent('updateDashboard'))
      window.dispatchEvent(new CustomEvent('updateSearchFiles'))
      window.dispatchEvent(new CustomEvent('updateFavorites'))
      window.dispatchEvent(new CustomEvent('bctable:updateTable', { detail: true }))
      window.dispatchEvent(new CustomEvent('fileupdate:undelete', { detail: fileDetails.id }))
      window.dispatchEvent(new CustomEvent('updateReports'))
    } catch (error: any) {
      // Handle errors (including e-signature cancellation)
      if (error?.message !== 'E-signature cancelled') {
        console.error('Make unobsolete failed:', error)
        toast.error(error?.message || error?.data?.message || 'Make unobsolete failed')
      }
    }
  }

  return (
    <Modal show={isOpen} handleClose={doClose} title={translate('BROWSER_UNDELETE_ITEM')}>
      <Modal.Body>
        {(options?.selectedWholeResult || isMultiselect()) && (
          <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>
            {translate('BROWSER_UNDELETE_CONFIRM_QUESTION_MULTIPLE_DOCS')}
          </Form.Label>
        )}
        {!options?.selectedWholeResult && !isMultiselect() && fileDetails.type && (
          <>
            <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>
              {translate('BROWSER_UNDELETE_CONFIRM_QUESTION_SINGLE_' + fileDetails.type)}
            </Form.Label>
            <p className="whitespaceprewrap" style={{ fontSize: '12px' }}>
              {fileDetails.name}
            </p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button data-action={'ok'} variant="link" onClick={doClose} id="f76408a2-37e5-4987-bdc0-791233585421">
          {translate('CANCEL')}
        </Button>
        <Button data-action={'ok'} className="btn btn-primary" onClick={save} id="a8d1df5d-afe9-48d3-808f-aa96fc9a6c2c">
          {translate('OK')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default MakeUnObsoleteModal
